import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Network, Clock, Euro, FileText } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const EFTIDigitalArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <Button 
          variant="ghost" 
          onClick={() => window.history.back()}
          className="mb-6 hover:bg-gray-100"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Industry Highlights
        </Button>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-purple-500 text-white">
                <Network className="h-3 w-3 mr-1" />
                Digitalization
              </Badge>
              <span className="text-gray-500 text-sm">November 2025</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              The Future of eFTI: Digital Transformation Accelerates EU Freight Transport
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              European freight transport digitalization reaches critical momentum as eFTI regulation 
              implementation accelerates, promising to revolutionize logistics efficiency and competitiveness.
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
              alt="Digital freight transport documentation and technology systems" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-purple-600" />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">600</div>
                <p className="text-sm text-gray-600">Days to mandatory</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Network className="h-5 w-5 mr-2 text-blue-600" />
                  Projects
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">3</div>
                <p className="text-sm text-gray-600">Major EU initiatives</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Euro className="h-5 w-5 mr-2 text-green-600" />
                  Investment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">€48M</div>
                <p className="text-sm text-gray-600">eFTI4LIVE budget</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">eFTI Revolution Overview</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              The Electronic Freight Transport Information (eFTI) regulation represents one of the most 
              significant digital transformations in European logistics history. With mandatory implementation 
              approaching in 600 days, the initiative promises to eliminate bureaucracy, increase efficiency, 
              and enhance the competitiveness of European freight transport.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Strategic Importance</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              According to Christian Hirte, Minister for Digital Infrastructure and Transport:
            </p>
            <blockquote className="border-l-4 border-purple-500 pl-6 italic text-gray-700 mb-6">
              "Beyond reducing bureaucracy, digitalisation also strengthens European competitiveness and 
              contributes to achieving climate targets. The future of European transport will be digitally integrated."
            </blockquote>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Three Major EU Projects</h3>
            <div className="space-y-6 mb-8">
              <div className="bg-purple-50 p-6 rounded-lg">
                <h4 className="font-semibold text-lg mb-3">eFTI4EU Project</h4>
                <p className="text-gray-700">
                  The foundational project that has reached maturity, transforming eFTI from a paper concept 
                  into a practical, interoperable solution through joint European efforts.
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="font-semibold text-lg mb-3">eFTI4ALL Project</h4>
                <p className="text-gray-700">
                  Focuses on accelerating digitalization implementation with tangible results based on 
                  practical examples and economic operator involvement.
                </p>
              </div>

              <div className="bg-green-50 p-6 rounded-lg">
                <h4 className="font-semibold text-lg mb-3">eFTI4LIVE Project</h4>
                <ul className="text-gray-700 space-y-1">
                  <li><strong>Coordinator:</strong> Spanish Ministry of Transport and Mobility</li>
                  <li><strong>Partners:</strong> 54 partners from 18 countries</li>
                  <li><strong>Budget:</strong> Nearly €48 million</li>
                  <li><strong>Focus:</strong> Platform certification and data interoperability systems</li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Germany's Portal Demonstration</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              During the Berlin conference, participants witnessed Germany's eFTI portal in action, 
              demonstrated by Toll Collect and the Federal Office of Mobility and Logistics. The live 
              demonstration showed how transport inspections can be carried out entirely using digital data, 
              proving the practical viability of the system.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Business Readiness and Competitive Advantage</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              According to Transport Innovation Association (TIA) head Rugilė Andziukevičiūtė-Buzė:
            </p>
            <blockquote className="border-l-4 border-blue-500 pl-6 italic text-gray-700 mb-6">
              "eFTI will be a real game-changer, driving the digitalisation of transport control operations. 
              Those who prepare first will win – this will open up opportunities for IT companies to create 
              new, convenient solutions for logistics and forwarding businesses."
            </blockquote>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Digital Document Revolution</h3>
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Documents Being Digitized:</h4>
              <ul className="space-y-2">
                <li><strong>eCMR:</strong> Electronic consignment notes</li>
                <li><strong>eCIM:</strong> Electronic rail transport documents</li>
                <li><strong>Control Documents:</strong> Digital inspection and compliance records</li>
                <li><strong>B2B Transactions:</strong> Business-to-business digital processes</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Implementation Timeline</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              With 600 days remaining until mandatory implementation for all EU institutions, businesses 
              have a critical window to prepare. Early adopters will gain significant competitive advantages 
              through improved efficiency, reduced costs, and enhanced compliance capabilities.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Industry Impact</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The eFTI regulation will fundamentally transform how freight transport operates across Europe. 
              Beyond reducing paperwork, it will create opportunities for new business models, improve 
              supply chain visibility, and enable more sophisticated logistics optimization.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Collaborative Success</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              As emphasized by Toll Collect's Managing Director Mark Erichsen: "eFTI will only work if 
              cooperation and coordination are ensured. The public and private sectors must work together." 
              This collaborative approach is essential for successful implementation across the diverse 
              European logistics landscape.
            </p>

            <div className="bg-gray-100 p-6 rounded-lg">
              <h4 className="font-semibold mb-2">Source Information:</h4>
              <p className="text-sm text-gray-600">
                Based on coverage of the Berlin eFTI conference and project developments from Trans.info. 
                <a href="https://trans.info/en/the-future-of-efti-434447" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline ml-1">
                  View original source
                </a>
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default EFTIDigitalArticle;